import axios from 'axios';
import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import { handleAllocationErrors } from '@/utils';
import { APIRoute, EP } from '@/api-endpoints';
import { LivingAllocationState, LivingAllocationOfferTypeValues, LivingRecipientDetails, LivingRecipientDetailsVxm, LivingRecipientDetailsVxmCurrentCumulative, LivingRecipientDetailsVxmClass } from '@/store/livingAllocations/types';
import { SaveResult } from '@/types';
import { TaskState, TaskType } from '@/store/tasks/types';

/**
 * Blank defaults for nested class1/class2 sub-documents in recipient VXM results
 *
 * @returns {LivingRecipientDetailsVxmClass} object containing defaults
 */
 function defaultRecipientDetailsVxmClass(): LivingRecipientDetailsVxmClass {
  return {
    result: null,
    messages: [],
    unacceptable_allele_group: [],
    unacceptable_allele_specific: [],
    unacceptable_epitopes: [],
    indeterminate_allele_group: [],
    indeterminate_allele_specific: [],
    indeterminate_epitopes: [],
    possible_allele_specific: [],
    untested: [],
    untested_epitopes: [],
  };
}

/**
 * Blank defaults for nested current/cumulative sub-documents in recipient VXM results
 *
 * @returns {RecipientDetailsVxmClass} object containing defaults
 */
 function defaultCurrentCumulative(): LivingRecipientDetailsVxmCurrentCumulative {
  return {
    result: null,
    class1: defaultRecipientDetailsVxmClass(),
    class2: defaultRecipientDetailsVxmClass(),
  };
}

/**
 * Sanitize response data to ensure it matches the expected type interface
 *
 * @param rawRecipientDetails recipient details response from the back-end
 * @returns {LivingRecipientDetails} sanitized details for display of allocation recipient details
 */
function sanitizeRecipientDetails(rawRecipientDetails: LivingRecipientDetails): LivingRecipientDetails {
  const rawVxm: LivingRecipientDetailsVxm = rawRecipientDetails.recipient.vxm;

  const sanitizedCurrent: LivingRecipientDetailsVxmCurrentCumulative = Object.assign({}, rawVxm.current || defaultCurrentCumulative());
  sanitizedCurrent.class1 = sanitizedCurrent.class1 || defaultRecipientDetailsVxmClass();
  sanitizedCurrent.class2 = sanitizedCurrent.class2 || defaultRecipientDetailsVxmClass();

  const sanitizedCumulative: LivingRecipientDetailsVxmCurrentCumulative = Object.assign({}, rawVxm.cumulative || defaultCurrentCumulative());
  sanitizedCumulative.class1 = sanitizedCumulative.class1 || defaultRecipientDetailsVxmClass();
  sanitizedCumulative.class2 = sanitizedCumulative.class2 || defaultRecipientDetailsVxmClass();

  const sanitizedVxm = Object.assign({}, rawVxm);
  sanitizedVxm.current = sanitizedCurrent;
  sanitizedVxm.cumulative = sanitizedCumulative;

  const result = Object.assign({}, rawRecipientDetails);
  result.recipient.vxm = sanitizedVxm;

  return result;
}

export const actions: ActionTree<LivingAllocationState, RootState> = {
  // Get Exclusion Rules given a clientId and organCode
  getExclusionRules({ commit, getters }, { clientId, organCode }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = APIRoute(EP.living_donors.allocations.exclusion_rules, [[':living_donor_id', clientId], [':organ_id', organCode]]);
      axios.get(url).then((response: any) => {
        const isSuccessful = response.data && !response.data.errors;
        if (isSuccessful) {
          // Commit rules if we have any
          commit('setExclusionRules', response.data.exclusion_rules);
          resolve();
        }
        resolve();
      }).catch((error: any) => {
        reject(error);
      });
    });
  },

  /**
   * Helper method to request an allocation given an endpoint and payload and then set things up
   * @param commit - The vuex commit method for this namespace
   * @param state
   * @param dispatch
   * @param url - The url to send the POST request to
   * @param payload - The paylod to attach
   */
    // request allocation then poll donor allocation section to find out if allocation finished
    requestAllocationTaskFromUrl({ commit, state, dispatch }, {url, payload}): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        axios.post(url, payload).then((response: any) => {
          const isSuccessful = response.data && !response.data.errors;
          if (isSuccessful) {
            // Note: Tp-12416 special case when we have a non-supercedable alloction and simply need to force refresh
            // however as we already do a refresh after a successful allocation then this is no longer necessary
            const allocationTask = response.data.allocation as TaskType;
            const taskId = allocationTask._id || undefined;
            Promise.all([
              dispatch('tasks/loadTask', taskId, { root: true })
            ]).then(() => {
              resolve();
            });
          } else if (response.data && response.data.errors) {
            // Build error result
            const errorResult = handleAllocationErrors(response.data.errors);
            // Reject with errors
            reject(errorResult);
          }
        }).catch((error: any) => {
          // check for response error code
          const response = error && error.response ? error.response : null;
          if (!response) reject(error);
          if (response.status == 401) {
            localStorage.removeItem('access_token');
            window.location.href = EP.user.login;
            reject(error);
          } else if (response.status == 403) {
            reject(error);
          }
          // Handle generic error message with an alert popup e.g. Network error
          if (!!error.message) {
            alert(error.message);
            // on clicking okay, refresh the page for the user
            location.reload();
          }
          reject(error);
        });
      });
    },

  // Run an allocation given a clientId and organCode
  runAllocation({ dispatch }, { clientId, organCode, payload }): Promise<void> {
    const url = APIRoute(EP.living_donors.allocations.create, [[':living_donor_id', clientId], [':organ_id', organCode]]);
    return dispatch('requestAllocationTaskFromUrl', { url: url, payload: payload});
  },
  // ReRun an allocation given a clientId and organCode
  reRunAllocation({ dispatch }, { clientId, organCode, payload, allocationId }): Promise<void> {
    const url = APIRoute(EP.living_donors.allocations.rerun, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
    return dispatch('requestAllocationTaskFromUrl', { url: url, payload: payload});
  },
  // Run an expedited allocation given a clientId and organCode
  runExpeditedAllocation({ dispatch }, { clientId, organCode, payload }): Promise<void> {
    const url = APIRoute(EP.living_donors.allocations.run_expedited, [[':living_donor_id', clientId], [':organ_id', organCode]]);
    return dispatch('requestAllocationTaskFromUrl', { url: url, payload: payload});
  },
  // Rerun an expedited allocation given a clientId, organCode and allocationId
  rerunExpeditedAllocation({ dispatch }, { clientId, organCode, payload, allocationId }): Promise<void> {
    const url = APIRoute(EP.living_donors.allocations.rerun_expedited, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
    return dispatch('requestAllocationTaskFromUrl', { url: url, payload: payload});
  },

  saveChecklist({commit, getters }, { clientId, organCode, allocationId, checklist}): Promise<SaveResult> {
    return new Promise<SaveResult>((resolve, reject) => {
      const payload={ checklist: checklist };
      const url = APIRoute(
        EP.living_donors.allocations.checklist.create,
        [[':living_donor_id', clientId], [':organ_id', organCode], [':allocation_id', allocationId]]
      );
      axios.post(url, payload).then((response: any) => {
        const isSuccessful = response.data && !response.data.errors;
        if (isSuccessful) {
          // Handle successful response
          resolve({ success: true, responseData: response.data });
        } else {
          // Build error result
          const errorResult = handleAllocationErrors(response.data.errors);
          // Reject with errors
          reject(errorResult);
        }
      }).catch((error: any) => {
        reject({ success: false, errorMessages: [error.message] });
      });
    });
  },
  getAllAllocations({commit, getters, dispatch, state}, {clientId, clearSelectedAllocation}): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      if(state.isLoadingAllocations) {
        resolve();
        return;
      }
      if(clearSelectedAllocation) {
        commit('clearAllocation');
      }
      commit('startLoadingAllocations');
      Promise.all([
        dispatch('getAllocations', { clientId: clientId, state: 'all' }),
        dispatch('getAllocations', { clientId: clientId, state: 'inactive' }),
        dispatch('getAllocations', { clientId: clientId, state: 'active' }),
      ]).finally(() => {
        setTimeout(() => {
          commit('stopLoadingAllocations');
          resolve();
        }, 1000);

        // emit to parent component we're done reloading the allocations
        // this.loadingAllocations = false;
      });
    });
  },

  // Get allocations by state, clientId (default state is all)
  getAllocations({ commit, getters }, { clientId, state, organCode }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = APIRoute(EP.living_donors.allocations.all, [[':living_donor_id', clientId]]);
      // Default to active allocations if we have no state supplied
      const opts = {
        allocations: {
          state: state || 'all',
          organ_code: organCode || null
        }
      };
      axios.post(url, opts).then((response: any) => {
        if (response.data.living_donor && response.data.living_donor.organ_allocations.length >= 0) {
          commit('setLastLoadedAllocationDonorId', response.data.living_donor.donor_id);
          switch(state) {
            case 'active':
              commit('setActiveAllocations', response.data.living_donor.organ_allocations);
              break;
            case 'inactive':
              commit('setInactiveAllocations', response.data.living_donor.organ_allocations);
              break;
            default:
              // Default is considered to be state all
              commit('setAllocations', response.data.living_donor.organ_allocations);
              break;
          }
        } else {
          commit('setAllocations', undefined);
        }
        resolve();
      }).catch((error: any) => {
        reject(error);
      });
    });
  },
  // Get allocation details given a clientId, organCode and allocationId
  getAllocation({ commit, getters, state }, { clientId, organCode, allocationId }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if(state.isCreatingAllocation) {
        resolve();
        return;
      }
      commit('startLoading');
      commit('clearAllocation');
      const url = APIRoute(EP.living_donors.allocations.show, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
      axios.get(url).then((response: any) => {
        const isSuccessful = response.data && !response.data.errors;
        if (isSuccessful) {
          commit('setAllocation', response.data.allocation);
          resolve();
        } else {
          // Build error result
          const errorResult = handleAllocationErrors(response.data.errors);
          // Reject with errors
          reject(errorResult);
        }
      }).catch((error: any) => {
        reject(error);
      }).finally(() => {
        commit('stopLoading');
      });
    });
  },
  // Get checklist details given an allocationId
  getChecklistDetails({ commit, getters }, { livingDonorId, organCode, allocationId}): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = APIRoute(
        EP.living_donors.allocations.checklist.show,
        [[':living_donor_id', livingDonorId], [':organ_id', organCode], [':allocation_id', allocationId]]
      );
      axios.get(url).then((response: any) => {
        const isSuccessful = response.data && !response.data.errors;
        if (isSuccessful) {
          commit('setChecklistDetails', response.data.checklist.checklists);
          resolve();
        } else {
          // Build error result
          const errorResult = handleAllocationErrors(response.data.errors);
          // Reject with errors
          reject(errorResult);
        }
      }).catch((error: any) => {
        reject(error);
      });
    });
  },
  // Get checklist history given an allocationId
  getChecklistHistory({ commit, getters }, { livingDonorId, organCode, allocationId}): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = APIRoute(
        EP.living_donors.allocations.checklist.history,
        [[':living_donor_id', livingDonorId], [':organ_id', organCode], [':allocation_id', allocationId]]
      );
      axios.get(url).then((response: any) => {
        const isSuccessful = response.data && !response.data.errors;
        if (isSuccessful) {
          commit('setChecklistHistory', response.data.checklists_history.checklists_history || []);
          resolve();
        } else {
          // Build error result
          const errorResult = handleAllocationErrors(response.data.errors);
          // Reject with errors
          reject(errorResult);
        }
      }).catch((error: any) => {
        reject(error);
      });
    });
  },
  // TODO: TECH_DEBT: this should return a standardized Promise<SaveResult> instead of <any>
  handleOffer({ commit, getters }, { url, payload, type }): Promise<any> {
    if (type === 'discontinue') commit('startDiscontinuingOneOffer');
    if (type === 'create') commit('startMakingOffer');
    return new Promise<any>((resolve, reject) => {
      axios.post(url, payload).then((response: any) => {
        // reject immediately if there is an error
        if (response.data && response.data.errors) {
          reject(response.data.errors.allocation_service || response.data.errors);
        }
        // DIAG: allocation response looks like { offer: actions: [{ ... }] }
        const responseData = response.data.offer;
        const responseAction = responseData.actions;
        // TODO: handle errors better for allocations
        let responseActionResult = { error: false, error_message: undefined };
        if (Array.isArray(responseAction) && responseAction.length > 0) {
          // TODO: Handle multiple errors
          responseActionResult = { ...responseAction[0] };
        }
        const isSuccessful = responseData && !responseActionResult.error;
        if (isSuccessful) {
          commit('setAllocation', responseData.allocation);
          resolve(response);
        } else {
          // Reject with errors
          reject(responseActionResult.error_message);
        }
      }).catch((error: any) => {
        reject(error);
      }).finally(() => {
        if (type === 'discontinue') commit('stopDiscontinuingOneOffer');
        if (type === 'create') commit('stopMakingOffer');
      });
    });
  },
  makeOffer({ dispatch }, { clientId, organCode, allocationId, offerDetails }): Promise<any> {
    let url: string;
    if (offerDetails.type === LivingAllocationOfferTypeValues.NoOffer) {
      url = APIRoute(EP.living_donors.allocations.offers.no_offer, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
    } else {
      url = APIRoute(EP.living_donors.allocations.offers.create, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
    }
    const payload = { allocation_offer: offerDetails };
    return dispatch('handleOffer', { url: url, payload: payload, type: 'create' });
  },
  respondOffer({ commit, getters }, { clientId, organCode, allocationId, responseDetails }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = APIRoute(EP.living_donors.allocations.offers.respond, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
      const payload = { responses: { actions: responseDetails } };
      axios.post(url, payload).then((response: any) => {
        // reject immediately if there is an error
        if (response.data && response.data.errors) {
          reject(response.data.errors.allocation_service || response.data.errors);
        }
        // DIAG: allocation response looks like { offer: actions: [{ ... }] }
        const responseData = response.data.offer;
        const responseAction = responseData.actions;
        // filter actions for any errors
        const actionErrors = responseAction.filter((item: any) => {
          // only return items with an error of true
          return item.error === true;
        });
        const actionErrorResponse: any[] = [];
        // if we have errors
        if (actionErrors.length > 0) {
          actionErrors.forEach((item: any) => {
            const errorResult = {
              recipient_id: item.recipient_id,
              error_code: item.error_code,
              error_message: item.error_message
            };
            actionErrorResponse.push(errorResult);
          });
        }
        // was this a successful response
        const isSuccessful = responseData && actionErrorResponse.length <= 0;
        if (isSuccessful) {
          commit('setAllocation', responseData.allocation);
          resolve();
        } else {
          // Reject with errors
          reject(actionErrorResponse);
        }
      }).catch((error: any) => {
        reject(error);
      });
    });
  },
  discontinueOffer({ dispatch }, { clientId, organCode, allocationId, discontinueDetails }): Promise<any> {
    const url = APIRoute(EP.living_donors.allocations.offers.discontinue_offers, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
    const payload = { allocation_offer: discontinueDetails };
    return dispatch('handleOffer', { url: url, payload: payload, type: 'discontinue' });
  },
  discontinueAllocationFromUrl({ commit, dispatch, state }, {url, donor_client_id, loadingCounter}): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      commit(`start${loadingCounter}`);
      axios.post(url).then((response: any) => {
        // reject immediately if there is an error
        if (response.data && response.data.errors) {
          reject(response.data.errors.allocation_service || response.data.errors);
          return;
        }
        const isSuccessful = response.data && !response.data.errors;
        if (isSuccessful) {
          commit('clearAllocation');
          Promise.all([
            dispatch('getAllAllocations', { clientId: donor_client_id }),
            dispatch('livingDonors/get', donor_client_id, { root: true })
          ]).then(() => {
            commit(`stop${loadingCounter}`);
            resolve();
          });
        } else {
          // Build error result
          const errorResult = handleAllocationErrors(response.data.errors);
          // Reject with errors
          reject(errorResult);
        }
      }).catch((error: any) => {
        reject(error);
      }).finally(() => {
        commit(`stop${loadingCounter}`);
      });
    });
  },
  discontinueAllocation({ dispatch, commit }, { clientId, organCode, allocationId }): Promise<void> {
    const url = APIRoute(EP.living_donors.allocations.offers.discontinue_allocation, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
    return dispatch('discontinueAllocationFromUrl', {url: url, donor_client_id: clientId, loadingCounter: 'DiscontinuingOneAllocation'});
  },
  discontinueOrgans({ dispatch }, { clientId }): Promise<void> {
    const url = APIRoute(EP.living_donors.allocations.discontinue_offers, [[':living_donor_id', clientId]]);
    return dispatch('discontinueAllocationFromUrl', {url: url, donor_client_id: clientId, loadingCounter: 'DiscontinuingAllAllocations'});
  },
  getDonorDetails({ commit, getters, state }, { livingDonorId, organCode, allocationId }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!state.donorDetails) {
        const url = APIRoute(EP.living_donors.allocations.donor_details, [[':living_donor_id', livingDonorId], [':organ_id', organCode], [':allocationId', allocationId]]);
        axios.get(url).then((response: any) => {
          const isSuccessful = response.data && !response.data.errors;
          if (isSuccessful) {
            // Commit rules if we have any
            commit('setDonorDetails', response.data.allocation);
            resolve();
          }
          resolve();
        }).catch((error: any) => {
          reject(error);
        });
      } else {
        resolve();
      }
    });
  },
  getRecipientDetails({ commit, getters }, { clientId, organCode, allocationId, recipientId, is2ndRankingEntry}): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = APIRoute(EP.living_donors.allocations.recipient_details, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
      const params = {
        recipient_id: recipientId,
        is_2nd_ranking_entry: is2ndRankingEntry
      };
      axios.get(url, { params }).then((response: any) => {
        const isSuccessful = response.data && !response.data.errors;
        if (isSuccessful) {
          // Sanitize response data to ensure it matches the expected type interface
          const recipient = response.data.allocation;
          const sanitized = sanitizeRecipientDetails(recipient);
          commit('setRecipientDetails', sanitized);
          resolve();
        } else {
          // Build error result
          const errorResult = handleAllocationErrors(response.data.errors);
          // Reject with errors
          reject(errorResult);
        }
        resolve();
      }).catch((error: any) => {
        reject(error);
      });
    });
  },
  // Generate an Allocation Report
  generateAllocationReport({ commit }, { payload }): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      commit('startGeneratingReport');
      if (!payload || !payload.allocation_client_id) reject();
      const url = APIRoute(EP.reports.allocation_report);
      axios.post(url, payload).then((response: any) => {
        // Do we have a url
        const isSuccessful = response.data && response.data.report;
        if (isSuccessful) resolve(response.data.report);
        reject(response);
      }).catch((error: any) => {
        reject(error);
      }).finally(() => {
        commit('stopGeneratingReport');
      });
    });
  },

  // Add Recipient to Allocation
  addRecipient({}, { clientId, organCode, allocationId, recipientId, transplantProgramId, reason }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!allocationId) reject();
      const url = APIRoute(EP.living_donors.allocations.add_recipient, [[':living_donor_id', clientId], [':organ_id', organCode], [':allocationId', allocationId]]);
      const payload = {
        recipient_id: recipientId,
        transplant_program_id: transplantProgramId,
        added_manually_reason: reason
      };
      axios.post(url, payload).then((response: any) => {
        const isSuccessful = response.data && !response.data.errors;

        if (isSuccessful) resolve();
        // Build error result
        const errorResult = handleAllocationErrors(response.data.errors);
        // Reject with errors
        reject(errorResult);
      }).catch((error: any) => {
        reject(error);
      });
    });
  },
  getExcludedRecipients({}, {clientId, organCode, allocationId}): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const url = APIRoute(EP.living_donors.allocations.excluded_recipients, [[':living_donor_id', clientId], [':organ_id', organCode], [':id', allocationId]]);
      axios.get(url).then((response: any) => {
        resolve(response);
      }).catch((error) => {
        reject(error);
      });
    });
  },

  // triggers an API call that will fetch Missing Recipients from Allocation Service
  // Missing Recipients are those recipients existing from the previous allocation with responded offers.
  getMissingRecipients({ commit, getters, state }, {allocation, organCode}): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // if allocation is not a re-run don't do anything.
      if (!allocation.supercedes_id) return resolve();
      const donorId = allocation.client_id;
      const allocationId = allocation._id;
      const url = APIRoute(EP.living_donors.allocations.missing_recipients, [[':living_donor_id', donorId], [':organ_id', organCode], [':id', allocationId]]);
      axios.get(url).then((response: any) => {
        commit('setMissingRecipients', response.data.missing_recipients.missing_recipients);
        resolve();
      }).catch((error) => {
        reject();
      });
    });
  }
};
